import { useState, useEffect } from 'react';
import { X, Gift, ArrowRight, Minimize2, Maximize2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import CountdownTimer from './CountdownTimer';
import { usePromotional } from '../../contexts/PromotionalContext';
import { cn } from '@/utils/cn';

interface FloatingPromoWidgetProps {
  className?: string;
}

const FloatingPromoWidget = ({ className }: FloatingPromoWidgetProps) => {
  const { config, isActive } = usePromotional();
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Show widget after 3 seconds of page load if promotion is active and enabled
  useEffect(() => {
    // Double-check that campaign is actually enabled before showing
    if (isActive && config.isEnabled && config.showFloatingWidget) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      // Ensure widget is hidden if campaign is disabled
      setIsVisible(false);
    }
  }, [isActive, config.isEnabled, config.showFloatingWidget]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleMinimizeToggle = () => {
    setIsMinimized(!isMinimized);
  };

  // Don't render if campaign is disabled at any level
  if (!config.isEnabled || !isActive || !config.showFloatingWidget || !isVisible) {
    return null;
  }

  return (
    <div className={cn(
      'fixed bottom-4 right-4 z-50 max-w-sm w-full mx-4 sm:mx-0 animate-slide-up',
      className
    )}>
      <div className="relative bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 text-white rounded-2xl shadow-2xl border-2 border-orange-300/50 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-pink-300/20 rounded-full blur-xl animate-pulse delay-500"></div>
        </div>
        
        {/* Header */}
        <div className="relative flex items-center justify-between p-3 border-b border-white/20">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 animate-bounce" aria-label="Gift icon" />
            <span className="font-bold text-sm">Limited Offer!</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleMinimizeToggle}
              className="p-1 hover:bg-white/20 rounded-full transition-all"
              aria-label={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? (
                <Maximize2 className="h-4 w-4" aria-label="Maximize2 icon" />
              ) : (
                <Minimize2 className="h-4 w-4" aria-label="Minimize2 icon" />
              )}
            </button>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/20 rounded-full transition-all"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-label="X icon" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        {!isMinimized && (
          <div className="relative p-4 space-y-3">
            <div className="text-center">
              <h3 className="text-lg font-bold mb-1">🔥 {config.discountPercentage}% OFF!</h3>
              <p className="text-sm opacity-90 leading-relaxed">
                All AI Testing Courses
              </p>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-center">
              <div className="text-xs font-medium mb-2 opacity-80">Use Coupon Code:</div>
              <div className="bg-white/30 rounded-md px-3 py-2 font-mono font-bold text-sm border border-white/30">
                {config.couponCode}
              </div>
            </div>
            
            <CountdownTimer 
              endDate={config.endDate}
              variant="hero"
              className="text-white text-center justify-center"
            />
            
            <div className="space-y-2">
              <Link
                to="/courses"
                className="w-full bg-white text-orange-600 px-4 py-2.5 rounded-lg font-bold text-center hover:bg-orange-50 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                onClick={() => setIsVisible(false)}
              >
                Get {config.discountPercentage}% Discount
                <ArrowRight className="h-4 w-4" aria-label="ArrowRight icon" />
              </Link>
              <p className="text-xs text-center opacity-75">
                ⚡ Don't miss this flash sale!
              </p>
            </div>
          </div>
        )}
        
        {/* Minimized state */}
        {isMinimized && (
          <div className="relative p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 animate-bounce" aria-label="Gift icon" />
                <span className="text-sm font-bold">{config.discountPercentage}% OFF</span>
              </div>
              <CountdownTimer 
                endDate={config.endDate}
                variant="compact"
                className="text-white text-xs"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingPromoWidget;